import { Component, ElementRef, HostBinding } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SkeletonService } from 'services/skeleton.service';

@Component({
    template: '<div></div>',
    standalone: false
})
export class SkeletonBaseComponent {
    skeletonTypeClass: SkeletonTypeClass;
    showSkeletonSubject: BehaviorSubject<boolean>;

    constructor(
        private elementRef: ElementRef,
        private skeletonService: SkeletonService
    ) {
        this.skeletonTypeClass = this.getSkeletonType();
        this.showSkeletonSubject = this.skeletonService.skeletonVisibleSubject;
    }

    @HostBinding('class')
    get hostClass() {
        if (this.showSkeletonSubject.value) {
            return `skeleton-${this.skeletonTypeClass}`;
        }
    }

    private getSkeletonType() {
        const className = Object.keys(classToSkeletonTypeMap).find((className) =>
            this.elementRef.nativeElement.classList.contains(className)
        );
        return classToSkeletonTypeMap[className];
    }
}

export enum SkeletonTypeClass {
    Banner = 'banner',
    Search = 'search',
    Filter = 'filter',
    OrderHistoryFilter = 'filter',
    StockToggle = 'stock-toggle',
    StockText = 'stock-text',
    ProductCard = 'product-card',
    ProductCardsList = 'product-cards-list',
    Sort = 'sort',
    CategoriesList = 'categories-list',
    CategoriesTitle = 'categories-title',
    OrderChips = 'order-chips',
    Logo = 'logo',
    Breadcrumbs = 'breadcrumbs',
    OrderSearchSelection = 'order-search-selection',
    SearchInput = 'search-input',
    BrandFilters = 'brand-filters',
    SearchBrandsInput = 'search-brands-input',
    FcBrandsTitle = 'fc-brands-title',
    FcBrandsList = 'fc-brands-list',
    ProductDetailBackLink = 'product-detail-back-link',
    OrderPdpGallery = 'order-pdp-gallery',
    ProductDetailHeaderBrand = 'product-detail-header-brand',
    ProductDetailHeaderContentWrapper = 'product-detail-header-content-wrapper',
    ProductDetailColorDropdown = 'product-detail-color-dropdown',
    ProductDetailDropdown = 'product-detail-dropdown',
    ProductDetailsProductPrice = 'product-details-product-price',
    ProductDetailAttributesAccordion = 'product-detail-attributes-accordion',
    ProductDetailMainMatrixTitle = 'product-detail-main-matrix-title',
    OrderMatrixWrapper = 'order-matrix-wrapper',
    OrderHistoryTitle = 'order-history-title',
    OrderHistoryTotalItems = 'order-history-total-items',
    OrderHistoryTable = 'order-history-table',
    List = 'list-item',
    FcBrandsDiscovery = 'fc-brands-discovery',
    FcInsightsFiltersBrands = 'fc-insights-filters-brands',
    FcInsightsArticlesListItem = 'fc-insights-articles-list-item',
    FcArticleDetailsTableContainer = 'fc-article-details-table-container',
    FcArticleDetailsHeaderTitle = 'fc-article-details-header-title',
    FcArticleDetailsImageContainer = 'fc-article-details-image-container',
    FcArticleDetailsAttributesMain = 'fc-article-details-attributes-main',
    FcArticleDetailsDateContainer = 'fc-article-details-date-container',
    FcArticleDetailsFooterContainer = 'fc-article-details-footer-container',
    FcArticleDetailsMatrixContainer = 'fc-article-details-matrix-container',
    FcRetailerFiltersList = 'fc-retailer-filters-list',
    FcRetailerFiltersSearchFormContainer = 'fc-retailer-filters-search-form-container',
    FcNavigationListSearchInput = 'fc-navigation-list-search-input',
    FcNavigationListSectionHeader = 'fc-navigation-list-section-header',
    FcNavigationListSectionList = 'fc-navigation-list-section-list',
    FcAddArticleV2ModalContentList = 'fc-add-article-v2-modal-content-list',
    BrandShopCover = 'fc-brand-shop-cover-image',
    FcProductInfoBox = 'fc-product-info-box',
    GalleryWrapper = 'fc-gallery-wrapper',
    FcProductPageSkeletonTitle = 'fc-product-page-skeleton-title',
    DownloadSettingsHeaderSkeleton = 'fc-download-settings-header-skeleton',
    DownloadSettingsOptionSkeleton = 'fc-download-settings-option-skeleton',
    DownloadSettingsSkeleton = 'fc-download-settings-skeleton',
    FcArAutoRestockFilter = 'fc-ar-auto-restock-filter',
    FcArAutoRestockRow = 'fc-ar-auto-restock-row',
    FcArAutoRestockSearch = 'fc-ar-auto-restock-search',
    FcArAutoRestockSorting = 'fc-ar-auto-restock-sorting',
    FcAutoRestockHeaderTabs = 'fc-auto-restock-header-tabs'
}

export const classToSkeletonTypeMap = {
    'fc-product-search-page-form': SkeletonTypeClass.Search,
    'fc-product-search-page-toggle-wrapper': SkeletonTypeClass.StockToggle,
    'fc-product-search-page-show-with-stock': SkeletonTypeClass.StockText,
    'fc-product-search-page-sort-by': SkeletonTypeClass.Sort,
    'fc-product-search-page-brand-products-card': SkeletonTypeClass.ProductCard,
    'fc-sidebar-filter-title': SkeletonTypeClass.CategoriesTitle,
    'fc-sidebar-categories-list': SkeletonTypeClass.CategoriesList,
    'skeleton-list-item': SkeletonTypeClass.List,
    'fc-product-search-page-filter': SkeletonTypeClass.Filter,
    'fc-order-chips': SkeletonTypeClass.OrderChips,
    'fc-sidebar-logo-container': SkeletonTypeClass.Logo,
    'fc-sidebar-breadcrumbs': SkeletonTypeClass.Breadcrumbs,
    'fc-order-search-selection-container': SkeletonTypeClass.OrderSearchSelection,
    'fc-brand-filters-search-form-container': SkeletonTypeClass.SearchInput,
    'fc-brand-filters-container': SkeletonTypeClass.BrandFilters,
    'search-brands-input': SkeletonTypeClass.SearchBrandsInput,
    'fc-brands-title': SkeletonTypeClass.FcBrandsTitle,
    'fc-brand-list': SkeletonTypeClass.FcBrandsList,
    'fc-product-detail-back-link': SkeletonTypeClass.ProductDetailBackLink,
    'fc-order-pdp-gallery': SkeletonTypeClass.OrderPdpGallery,
    'fc-product-detail-header-brand': SkeletonTypeClass.ProductDetailHeaderBrand,
    'fc-product-detail-header-content-wrapper': SkeletonTypeClass.ProductDetailHeaderContentWrapper,
    'fc-product-detail-color-dropdown': SkeletonTypeClass.ProductDetailColorDropdown,
    'fc-product-detail-dropdown': SkeletonTypeClass.ProductDetailDropdown,
    'fc-product-detail-product-price': SkeletonTypeClass.ProductDetailsProductPrice,
    'fc-product-detail-attributes-accordion': SkeletonTypeClass.ProductDetailAttributesAccordion,
    'fc-product-detail-main-matrix-title': SkeletonTypeClass.ProductDetailMainMatrixTitle,
    'fc-product-detail-article-container-wrapper': SkeletonTypeClass.OrderMatrixWrapper,
    'fc-order-history-title': SkeletonTypeClass.OrderHistoryTitle,
    'fc-order-history-total-items': SkeletonTypeClass.OrderHistoryTotalItems,
    'fc-order-history-table-wrapper': SkeletonTypeClass.OrderHistoryTable,
    'fc-product-search-page-brand-products': SkeletonTypeClass.ProductCardsList,
    'fc-order-history-filter': SkeletonTypeClass.OrderHistoryFilter,
    'fc-order-history-search-bar': SkeletonTypeClass.Search,
    'fc-brand-discovery': SkeletonTypeClass.FcBrandsDiscovery,
    'fc-insights-filters-brands': SkeletonTypeClass.FcInsightsFiltersBrands,
    'fc-insights-articles-list-item': SkeletonTypeClass.FcInsightsArticlesListItem,
    'fc-article-details-table-container': SkeletonTypeClass.FcArticleDetailsTableContainer,
    'fc-article-details-header-title': SkeletonTypeClass.FcArticleDetailsHeaderTitle,
    'fc-article-details-image-container': SkeletonTypeClass.FcArticleDetailsImageContainer,
    'fc-article-details-attributes-main': SkeletonTypeClass.FcArticleDetailsAttributesMain,
    'fc-article-details-date-container': SkeletonTypeClass.FcArticleDetailsDateContainer,
    'fc-article-details-footer-container': SkeletonTypeClass.FcArticleDetailsFooterContainer,
    'fc-article-details-matrix-container': SkeletonTypeClass.FcArticleDetailsMatrixContainer,
    'fc-retailer-filters-list': SkeletonTypeClass.FcRetailerFiltersList,
    'fc-retailer-filters-search-form-container': SkeletonTypeClass.FcRetailerFiltersSearchFormContainer,
    'fc-navigation-list-search-input': SkeletonTypeClass.FcNavigationListSearchInput,
    'fc-navigation-list-section-header': SkeletonTypeClass.FcNavigationListSectionHeader,
    'fc-navigation-list-section-list': SkeletonTypeClass.FcNavigationListSectionList,
    'fc-add-article-v2-modal-content-list': SkeletonTypeClass.FcAddArticleV2ModalContentList,
    'fc-brand-shop-cover-image': SkeletonTypeClass.BrandShopCover,
    'fc-product-info-box': SkeletonTypeClass.FcProductInfoBox,
    'fc-gallery-wrapper': SkeletonTypeClass.GalleryWrapper,
    'fc-gallery-page-wrapper': SkeletonTypeClass.GalleryWrapper,
    'fc-product-page-skeleton-title': SkeletonTypeClass.FcProductPageSkeletonTitle,
    'fc-download-settings-header-skeleton': SkeletonTypeClass.DownloadSettingsHeaderSkeleton,
    'fc-download-settings-option-skeleton': SkeletonTypeClass.DownloadSettingsOptionSkeleton,
    'fc-download-settings-skeleton': SkeletonTypeClass.DownloadSettingsSkeleton,
    'fc-auto-restock-articles-filters-dropdown-section': SkeletonTypeClass.FcArAutoRestockFilter,
    'fc-ar-auto-restock-filter': SkeletonTypeClass.FcArAutoRestockFilter,
    'fc-auto-restock-articles-list': SkeletonTypeClass.FcArAutoRestockRow,
    'fc-auto-restock-articles-filters-input': SkeletonTypeClass.FcArAutoRestockSearch,
    'fc-auto-restock-articles-sorting-and-tabs-container': SkeletonTypeClass.FcArAutoRestockSorting,
    'fc-auto-restock-header-tabs': SkeletonTypeClass.FcAutoRestockHeaderTabs
};
